import { rd, RemoteData } from '@passionware/monads';
import { X } from 'lucide-react';
import {
  createContext,
  forwardRef,
  Fragment,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { cn } from 'v5/platform/dom/cn';
import { DimmedContainer } from '../../v4/shared/components/forms/input/primitvies/DimmedContainer';
import { badgeVariants } from './Badge';
import { Button } from './Button';
import {
  Combobox,
  ComboboxButton,
  ComboboxButtonProps,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxQueryRenderer,
  ComboboxTrigger
} from './Combobox';

interface MultiSelectContextValue {
  value: string[];
  handleSelect: (value: string) => void;
}

const MultiSelectContext = createContext<MultiSelectContextValue | null>(null);

export interface MultiSelectProps {
  value?: string[];
  onValueChange?: (value: string[]) => void;
  children: ReactNode;
}

export function MultiSelect({
  value = [],
  onValueChange,
  children
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);

  const handleSelect = useCallback(
    (selectedValue: string) => {
      const newValue = value.includes(selectedValue)
        ? value.filter(v => v !== selectedValue)
        : [...value, selectedValue];
      onValueChange?.(newValue);
    },
    [value, onValueChange]
  );

  const contextValue = useMemo(
    () => ({
      value,
      handleSelect
    }),
    [value, handleSelect]
  );

  return (
    <MultiSelectContext.Provider value={contextValue}>
      <Combobox open={open} onOpenChange={setOpen}>
        {children}
      </Combobox>
    </MultiSelectContext.Provider>
  );
}

export interface MultiSelectTriggerProps
  extends Pick<ComboboxButtonProps, 'variant' | 'size'> {
  children: (values: string[]) => RemoteData<ReactNode[]>;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  /** Replace a selected value's presentation; return undefined to use the default badge. */
  renderValue?: (props: {
    value: string;
    label: ReactNode;
    readOnly?: boolean;
    removeButton: ReactNode;
  }) => ReactNode;
}

export const MultiSelectTrigger = forwardRef<
  HTMLButtonElement,
  MultiSelectTriggerProps
>(function MultiSelectTrigger(
  {
    children,
    placeholder = 'Select options...',
    disabled,
    size = 'md',
    variant,
    readOnly,
    renderValue
  },
  ref
) {
  const context = useContext(MultiSelectContext);
  if (!context) {
    throw new Error('MultiSelectTrigger must be used within MultiSelect');
  }

  const { value } = context;

  const childrenData = children(value);

  return (
    <ComboboxTrigger asChild disabled={disabled}>
      <ComboboxButton
        ref={ref}
        className="w-full justify-start"
        size={size}
        variant={variant}
        readOnly={readOnly}
        disabled={disabled || readOnly}
      >
        {value.length > 0 ? (
          <DimmedContainer
            className="min-w-0"
            shouldDim={rd.isPlaceholderData(childrenData)}
          >
            {rd
              .journey(childrenData)
              .wait(null)
              .catch(() => null)
              .map(labels => (
                <div className={cn('flex flex-wrap gap-1', '-my-0.5')}>
                  {labels.map((label, index) => {
                    const removeButton = !readOnly && (
                      <Button
                        variant="ghost"
                        tabIndex={-1}
                        size="sm"
                        className="h-4 w-4 shrink-0 p-0 hover:bg-transparent"
                        onClick={e => {
                          e.stopPropagation();
                          context.handleSelect(value[index]);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    );
                    return (
                      <Fragment key={value[index]}>
                        {renderValue?.({
                          value: value[index],
                          label,
                          readOnly,
                          removeButton
                        }) ?? (
                          <div
                            // A label with no spaces still wraps instead of widening the trigger.
                            className={cn(
                              'flex min-w-0 items-center gap-1 [overflow-wrap:anywhere]',
                              badgeVariants({
                                variant: 'gray',
                                tone: 'subtle',
                                size: 'md'
                              })
                            )}
                            aria-readonly={readOnly}
                          >
                            {label}
                            {removeButton}
                          </div>
                        )}
                      </Fragment>
                    );
                  })}
                </div>
              ))}
          </DimmedContainer>
        ) : (
          <span className="text-fg-muted">{placeholder}</span>
        )}
      </ComboboxButton>
    </ComboboxTrigger>
  );
});

export interface MultiSelectContentProps {
  children: ReactNode;
  shouldFilter?: boolean;
}

export function MultiSelectContent({
  children,
  shouldFilter = false
}: MultiSelectContentProps) {
  const context = useContext(MultiSelectContext);
  if (!context) {
    throw new Error('MultiSelectTrigger must be used within MultiSelect');
  }

  const { value, handleSelect } = context;
  return (
    <ComboboxContent shouldFilter={shouldFilter}>
      <ComboboxInput
        onKeyDown={e => {
          if (
            e.key === 'Backspace' &&
            e.currentTarget.selectionStart === 0 &&
            value.length > 0
          ) {
            handleSelect(value[value.length - 1]);
          }
        }}
        placeholder="Search..."
      />
      <ComboboxList>{children}</ComboboxList>
    </ComboboxContent>
  );
}

export interface MultiSelectItemProps {
  value: string;
  children: ReactNode;
  disabled?: boolean;
  closeOnSelect?: boolean;
}

export function MultiSelectItem({
  value,
  children,
  disabled,
  closeOnSelect = false
}: MultiSelectItemProps) {
  const context = useContext(MultiSelectContext);
  if (!context) {
    throw new Error('MultiSelectItem must be used within MultiSelect');
  }

  const { value: selectedValues, handleSelect } = context;
  const isSelected = selectedValues.includes(value);

  return (
    <ComboboxItem
      value={value}
      onSelect={() => handleSelect(value)}
      disabled={disabled}
      isSelected={isSelected}
      closeOnSelect={closeOnSelect}
    >
      {children}
    </ComboboxItem>
  );
}

export const MultiSelectQueryRenderer = ComboboxQueryRenderer;
export const MultiSelectGroup = ComboboxGroup;
export const MultiSelectEmpty = ComboboxEmpty;
